import ModalWrapper from  '@/components/modals/ModalWrapper';
import { optionsClean } from  '@/helpers/Insights';
import { buildCustomDate, today, yesterday } from  '@/helpers/Dates';
import { BuildPath } from  '@/helpers/Paths';
import { Truncate } from  '@/helpers/Text';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { FilterPage, IDateRange, IExistingItem, IOption, ISearchFilter } from  '@/interfaces/insightsp2';
import { container, TYPES } from "@/inversifyconfig/container.config";
import { useEffect, useRef, useState } from 'react';
import ExistingItemModel from '../../modals/ExistingItemModal';
import DateRange from './DateRange';
import CustomSelect from './CustomSelect';

import  '@/css/calendar.scss';
import { Tooltip as ReactTooltip } from 'react-tooltip';


function ItemButton({contentItem, clearCurrentItem, contentBtnClick}: {contentItem: IExistingItem[], clearCurrentItem: (item?:IExistingItem) => void, contentBtnClick: () => void}):JSX.Element {

    return(
        <>
        {contentItem.map(item => 
             <button className="highlighted" key={`${item.id}`} aria-label='Download'>
                <img alt="" src={`${BuildPath()}/images/buttons/close-white.svg`} onClick={() => clearCurrentItem(item)} />
                {Truncate(item.title, 10)}
              </button>
        )}
       
        <button 
            className={`add-content-item-button ${contentItem.length === 10 ? 'disabled' : ''}`}
            data-tip
            data-tooltip-id='contentError'
            data-event='mouseenter'
            data-event-off='mouseleave'
            onClick={contentItem.length !== 10 ? contentBtnClick : (e) => {e.preventDefault()}}
        >
            <span className="open">+ Content item</span>
        </button>

        <ReactTooltip id='contentError'  className={`thrive-tooltip ${contentItem.length === 10 ? '' : 'invisible'}`} >
            <span className='content-error-tooltip'>You can only compare up to 10 content items at a time</span>
        </ReactTooltip>
    
        </>
       
    )
}

const SearchFilter = ({page, setSearchFilterOptions} : {page:FilterPage, setSearchFilterOptions:(options:ISearchFilter) => void}) => {

    // State data
    const [breakdownDates1, setBreakdownDates1] = useState<IDateRange>(today());
    const [breakdownDates2, setBreakdownDates2] = useState<IDateRange>(yesterday());

    const [workspaces, setWorkspaces] = useState<IOption[]>([]);
    const [selectedWorkSpace, setSelectedWorkSpace] = useState<IOption>(null);

    const [userGroups, setUserGroups] = useState<IOption[]>([]);
    const [allUserGroups, setAllUserGroups] = useState<IOption[]>([]);
    const [selectedUserGroups, setSelectedUserGroups] = useState<IOption[]>([]);

    const [allWorkspaces, setAllWorkspace] = useState<any[]>([]);

    const [selectionsChanged, setSelectionsChanged] = useState<boolean>(false);

    // Existing content
    const [contentItem, setContentItem] = useState<IExistingItem[]>([]);
    const [openModal, setModal] = useState(false);

    // Reset
    const [resetDates, setResetDates]= useState<boolean>(false);

    // Refs
    const workspaceSelectRef = useRef(null);
    const usergroupSelectRef = useRef(null);

    const dateSelectorRefOne = useRef(null);
    const dateSelectorRefTwo = useRef(null);

    const searchOptions = useRef(null);

    

    // Initial load
    useEffect(() => {

        // Workspaces
        container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/workspaces`).then((x) => {
       
            // Save all
            setAllWorkspace(x.data.spaces);

            // All groups
            setupAllUserGroups(x.data.spaces);

            const options:IOption[] = [];

            x.data.spaces.forEach((y:any) => {
                options.push({
                    value:y.guid,
                    label:y.title
                });
            });

            const cleaned = optionsClean(options);

            // Default all
            cleaned.unshift({
                value:null,
                label:'All Workspaces'
            })

            setWorkspaces(cleaned);
           
        })
        .catch((error) => {
            console.log(`Workspaces error: ${error}`);
        })   
    }, []);

    // Workspace change
    useEffect(() => {

        if (allWorkspaces && selectedWorkSpace && selectedWorkSpace.value){

            const selected = allWorkspaces.filter(x => x.guid === selectedWorkSpace.value);
         
            if (selected.length && selected[0].userGroups){

                const options:IOption[] = [];

                selected[0].userGroups.forEach(group => {
                    
                    options.push({
                        value:group.id,
                        label:group.title
                    });

                });

                if (options.length){
                    setUserGroups(optionsClean(options));
                }
                else{
                    // No results clear
                    setUserGroups([]);
                }
            }
            
        }

    }, [selectedWorkSpace]);

    // Any selections changed enable search button
    useEffect(() => {

        const filterOptions:ISearchFilter = {
            date1:breakdownDates1,
            date2:breakdownDates2,
            selectedWorkspace:selectedWorkSpace,
            selectedUserGroups:selectedUserGroups,
            existingItem:contentItem
        }

        if (searchOptions.current && JSON.stringify(searchOptions.current) !== JSON.stringify(filterOptions)){
            setSelectionsChanged(true);
        }

        searchOptions.current = filterOptions;

    }, [breakdownDates1, breakdownDates2, selectedWorkSpace, selectedUserGroups, contentItem])

    function setupAllUserGroups(data:any){

        if (!data) return; 

        const options:IOption[] = [];

        data.forEach((space:any) => {

            space.userGroups.forEach(group => {

                // Already added?
                if (!options.find(x => x.value === group.id)){

                    options.push({
                        value:group.id,
                        label:group.title
                    });
                }

            });
        });

        const cleaned = optionsClean(options);
        
        setAllUserGroups(cleaned);
    }

    // Existing content
    function contentBtnClick():void {
        setModal(true)      
    }

    // Clear existing content 
    function clearCurrentItem(itemToDelete: IExistingItem = undefined):void {
        if (itemToDelete) {
            setContentItem(contentItem.filter(item => item.id !== itemToDelete.id));
        }
        else {
            setContentItem([]);
        }
        
    }  

    // Reset form
    function resetForm():void {

        // Dates
        dateSelectorRefOne.current.clear();
        dateSelectorRefTwo.current.clear();

        setBreakdownDates1(today());
        setBreakdownDates2(yesterday());

        // Workspaces
        if (workspaceSelectRef.current){
            workspaceSelectRef.current.clear();
            setSelectedWorkSpace(null);
        }

        // Usergroups
        setSelectedUserGroups([]);
        usergroupSelectRef.current.clear();

        // Content item
        setContentItem([]);

    }
    
    // Exsting content button

    // Search
    function updateSearch():void {

        // Set in content page
        const filterOptions:ISearchFilter = {
            date1:breakdownDates1,
            date2:breakdownDates2,
            selectedWorkspace:selectedWorkSpace,
            selectedUserGroups:selectedUserGroups,
            existingItem:contentItem
        }

        setSearchFilterOptions(filterOptions);

        setSelectionsChanged(false);
    }

    // Workspace change
    function selectedWorkSpaceChange(options:IOption[]){

        // Save
        if (options.length){
            setSelectedWorkSpace(options[0]);
        }

        // Clear content item
        clearCurrentItem();

        // Clear groups
        setSelectedUserGroups([]);
        usergroupSelectRef.current.clear();
    }

    // Current groups
    function currentGroups(): IOption[]{

        function addAllDefault(groups: IOption[]): IOption[]{

            const options:IOption[] = [...groups];

            // Default all
            options.unshift({
                value:null,
                label:'All User Groups'
            })

            return options;
        }

        if(selectedWorkSpace && selectedWorkSpace.value){
            return addAllDefault(userGroups)
        }

        return addAllDefault(allUserGroups);
    }

    function addContentItem(item: IExistingItem[]) {
        setContentItem([...item]);
    }


    function getContentItemHeight() {
        if(contentItem.length <= 1) {
            return 64.5
        } 
        else if (contentItem.length > 1 && contentItem.length < 7) {
            return 114.5
        }
        
        return  165.5 
    }
        

    return (        
        <>
            <section className="content-options">

                <h2>Showing insights for:</h2>

                <div className="row-1">

                    <div className="cal-container">
                        
                        <DateRange 
                            defaultDate={{ value: 'today', label: `Today (${buildCustomDate(today().dateFrom)})` }} 
                            setDateRange={setBreakdownDates1} 
                            reset={resetDates} 
                            setReset={setResetDates} 
                            showDateNum={true} 
                            ref={dateSelectorRefOne}
                        />
                        
                    </div>

                    <span>vs</span>

                    <div className="cal-container">              

                        <DateRange 
                            defaultDate={{ value: 'yesterday', label: `Yesterday (${buildCustomDate(yesterday().dateFrom)})` }} 
                            setDateRange={setBreakdownDates2} 
                            reset={resetDates} 
                            setReset={setResetDates} 
                            showDateNum={true} 
                            ref={dateSelectorRefTwo}
                        />

                    </div>

                </div>

                <div className="row-2" style={{height: getContentItemHeight()}}>

                    {page === FilterPage.content && 
                        <div className="select-label react-selector">

                            <span>WORKSPACE(S)</span>

                            <CustomSelect 
                                options={workspaces} 
                                optionsChange={(spaces) => selectedWorkSpaceChange(spaces)} 
                                ref={workspaceSelectRef} 
                                multi={false} 
                                showCalendar={false} 
                                placeholder="All Workspaces"
                                defaultSelectedIndex={0}
                            />

                        </div>
                    }

                    <div className="select-label react-selector multi-user-group">

                        <span>USER GROUP(S)</span>
                   
                        <CustomSelect 
                            options={currentGroups()} 
                            optionsChange={setSelectedUserGroups} 
                            ref={usergroupSelectRef} 
                            multi={true} 
                            showCalendar={false} 
                            placeholder="All User Groups"
                            defaultSelectedIndex={0}
                        />

                    </div>

                    {page === FilterPage.content &&
                        <div className="select-label content-item">
                            <span className='content-item-title'>CONTENT ITEM</span>

                            <div className='item-button-container'>
                                <span className='spacer'></span>
                                <ItemButton contentItem={contentItem} contentBtnClick={contentBtnClick} clearCurrentItem={clearCurrentItem} /> 
                            </div>
                        </div>
                    }

                </div>

                <div className="row-3">
                    <button onClick={updateSearch} className="in-button" disabled={!selectionsChanged}>Update</button>
                    <span onClick={resetForm}>Reset search</span>
                </div>

            </section>
           
            <ModalWrapper isOpen={openModal} onRequestClose={() => setModal(false)} className="existing-content-model insights-modal">
                <ExistingItemModel
                    setContentItems={addContentItem}
                    close={() => setModal(false)}
                    selectedWorkSpace={selectedWorkSpace}
                    selectedContentItems={contentItem} isMultiSelect
                    buttonText={'Select Content'}
                    showFilterBy
                    isInsights
                />
            </ModalWrapper> 
        </>   
    );
};

export default SearchFilter;